import {
  DefaultMetricOptions,
  EntityLinks,
  Goal,
  GoalProgressCount,
  ID,
} from '@griply/types';

import { getLatestMetricValue } from './get-latest-metric-entry';
import { sumSubGoalProgress } from './get-sub-goal-progress';
import { buildParentToChildrenMap, getSubGoals } from './get-sub-goals';

const emptyCount: GoalProgressCount = { completed: 0, total: 0 };

export type BuildSubGoalProgressCountsOptions = {
  links: EntityLinks;
};

// Writes into `counts` in place: a sub-goal's own task count must already be there to weigh it.
export const buildSubGoalProgressCounts = (
  goals: Goal[],
  counts: Map<ID, GoalProgressCount>,
  { links }: BuildSubGoalProgressCountsOptions,
): Map<ID, GoalProgressCount> => {
  const subGoalProgressGoals = goals.filter(
    ({ metric }) => metric?.id === DefaultMetricOptions.SubGoalProgress,
  );
  if (!subGoalProgressGoals.length) {
    return counts;
  }

  // The full parent map, not the rollup's deepest-parent one: the same sub-goal set `useSubGoals` resolves.
  const parentToChildrenMap = buildParentToChildrenMap(goals, links);

  subGoalProgressGoals.forEach((goal) => {
    // Archived children are pruned: counting them as permanent zeroes makes the parent uncompletable.
    // Completed and frozen ones stay in — dropping those reports a goal whose sub-goals are all done as 0%.
    const subGoals = getSubGoals(goal.id, {
      goals,
      recursive: true,
      links,
      parentToChildrenMap,
    }).filter(({ archivedAt }) => !archivedAt);

    counts.set(goal.id, {
      ...(counts.get(goal.id) ?? emptyCount),
      subGoals: {
        progress: sumSubGoalProgress(subGoals, (subGoal) => ({
          isCompleted: !!subGoal.completedAt,
          taskCount: counts.get(subGoal.id),
          metricValue: getLatestMetricValue(subGoal.metric),
        })),
        count: subGoals.length,
      },
    });
  });

  return counts;
};
